<script setup>
import { onMounted, ref } from 'vue'
import { fetchSMTPSettings, sendSMTPTestEmail, updateSMTPSettings } from '../services/adminService.js'
import { parseActionError } from '../utils/adminUi.js'

const host = ref('')
const port = ref('')
const username = ref('')
const password = ref('')
const fromEmail = ref('')
const useTls = ref(false)
const useSsl = ref(false)
const recipientEmail = ref('')

const loadingConfig = ref(false)
const savingConfig = ref(false)
const sendingTest = ref(false)

const loadError = ref('')
const saveError = ref('')
const testError = ref('')

const saveMessage = ref('')
const testMessage = ref('')

const loadSettings = async () => {
  loadingConfig.value = true
  loadError.value = ''

  try {
    const data = await fetchSMTPSettings()
    host.value = String(data?.host ?? '')
    port.value = data?.port === null || data?.port === undefined ? '' : String(data.port)
    username.value = String(data?.username ?? '')
    fromEmail.value = String(data?.from_email ?? '')
    useTls.value = Boolean(data?.use_tls)
    useSsl.value = Boolean(data?.use_ssl)
    // Never render stored password back into the UI.
    password.value = ''
  } catch (err) {
    loadError.value = parseActionError(err, 'Failed to load SMTP settings.')
  } finally {
    loadingConfig.value = false
  }
}

const saveSettings = async () => {
  savingConfig.value = true
  saveError.value = ''
  saveMessage.value = ''

  try {
    const payload = {
      host: host.value.trim(),
      port: Number(port.value),
      username: username.value.trim(),
      from_email: fromEmail.value.trim(),
      use_tls: Boolean(useTls.value),
      use_ssl: Boolean(useSsl.value),
    }

    if (password.value.trim()) {
      payload.password = password.value
    }

    await updateSMTPSettings(payload)
    password.value = ''
    saveMessage.value = 'SMTP settings saved.'
  } catch (err) {
    saveError.value = parseActionError(err, 'Failed to save SMTP settings.')
  } finally {
    savingConfig.value = false
  }
}

const sendTest = async () => {
  sendingTest.value = true
  testError.value = ''
  testMessage.value = ''

  try {
    await sendSMTPTestEmail(recipientEmail.value.trim())
    testMessage.value = 'Test email sent.'
  } catch (err) {
    testError.value = parseActionError(err, 'Failed to send test email.')
  } finally {
    sendingTest.value = false
  }
}

onMounted(() => {
  loadSettings()
})
</script>

<template>
  <section class="settings-wrap">
    <h1>SMTP Settings</h1>

    <p v-if="loadError" class="error">{{ loadError }}</p>

    <form class="panel form" @submit.prevent="saveSettings">
      <label class="form-label">
        Host
        <input v-model="host" class="form-input" type="text" autocomplete="off" required />
      </label>

      <label class="form-label">
        Port
        <input v-model="port" class="form-input" type="number" min="1" autocomplete="off" required />
      </label>

      <label class="form-label">
        Username
        <input v-model="username" class="form-input" type="text" autocomplete="off" required />
      </label>

      <label class="form-label">
        Password
        <input
          v-model="password"
          class="form-input"
          type="password"
          autocomplete="new-password"
          placeholder="Leave blank to keep existing password"
        />
      </label>

      <label class="form-label">
        From email
        <input v-model="fromEmail" class="form-input" type="email" autocomplete="off" required />
      </label>

      <label class="checkbox-label">
        <input v-model="useTls" type="checkbox" />
        use_tls
      </label>

      <label class="checkbox-label">
        <input v-model="useSsl" type="checkbox" />
        use_ssl
      </label>

      <button class="btn btn-primary" type="submit" :disabled="loadingConfig || savingConfig || sendingTest">
        {{ savingConfig ? 'Saving...' : 'Save settings' }}
      </button>

      <p v-if="saveMessage" class="success">{{ saveMessage }}</p>
      <p v-if="saveError" class="error">{{ saveError }}</p>
    </form>

    <form class="panel form" @submit.prevent="sendTest">
      <h2>Send test email</h2>

      <label class="form-label">
        recipient_email
        <input v-model="recipientEmail" class="form-input" type="email" autocomplete="off" required />
      </label>

      <button class="btn btn-secondary" type="submit" :disabled="loadingConfig || savingConfig || sendingTest">
        {{ sendingTest ? 'Sending...' : 'Send test email' }}
      </button>

      <p v-if="testMessage" class="success">{{ testMessage }}</p>
      <p v-if="testError" class="error">{{ testError }}</p>
    </form>
  </section>
</template>

<style scoped>
.settings-wrap {
  max-width: 720px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  gap: 1rem;
}

.form {
  display: grid;
  gap: 0.75rem;
}

.form-label {
  display: grid;
  gap: 0.35rem;
  color: #111827;
}

.form-input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.5rem 0.6rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #111827;
}

.success {
  color: #166534;
  background: #dcfce7;
  border-radius: 6px;
  padding: 0.5rem;
}

.error {
  color: #991b1b;
  background: #fee2e2;
  border-radius: 6px;
  padding: 0.5rem;
}
</style>

